import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./patient-requisition.css";
import OrderPopup from "../order-popup/order-popup";
import { useNavigate, useSearchParams } from "react-router-dom";
import UploadRequisitionForm from "../upload-requisition-form/upload-requisition-form";
import Loading from "../loading/Loading";
import { ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import CurrentDiagnosis from "../current-diagnosis/current-diagnosis.jsx";
import ProviderRequisition from "../provider-requisition/provider-requisition.jsx";
import { GlobalContext } from "../../GlobalContext";
import { Modal } from "@mantine/core";
import PatientConsentForm from "../consent-forms/patient-consent-form";
import OrderSummaryCard from "../order-summary-card/order-summary-card";

const formatPrice = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "₦0.00";
  }
  let numAmount;
  if (typeof amount === 'string') {
    const cleanAmount = amount.replace(/[₦,]/g, '').replace(/\.00$/, '');
    numAmount = parseFloat(cleanAmount);
  } else {
    numAmount = parseFloat(amount);
  }
  if (isNaN(numAmount)) {
    return "₦0.00";
  }
  return `₦${numAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const PatientRequisition = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const test_slug = searchParams.get("test_slug"); // Extract test_slug from query parameters
  const order_id = searchParams.get("order_id"); // Extract order_id from query parameters
  const { userData: globalUserData } = useContext(GlobalContext);

  const [userRole, setUserRole] = useState("individual");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [category, setCategory] = useState("oncology");
  const [providerPatientId, setProviderPatientId] = useState(null);
  // const [personalCancerHistory, setPersonalCancerHistory] = useState([
  //   { cancerType: "", cancerTypeSpec: "", age: "" },
  // ]);
  const [oncologyDiagnosisHistory, setOncologyDiagnosisHistory] = useState([
    { cancerType: "", cancerTypeSpec: "", age: "" },
  ]);
  const [hereditaryDiagnosisHistory, setHereditaryDiagnosisHistory] = useState([
    { cancerType: "", cancerTypeSpec: "", age: "" },
  ]);
  const [familyCancerHistory, setFamilyCancerHistory] = useState([
    {
      relationship: "",
      cancerType: "",
      cancerTypeSpec: "",
      ageRange: "",
      maternalPaternal: "",
      relativeAvailable: "",
    },
  ]);
  const [familyDiseaseHistory, setFamilyDiseaseHistory] = useState([
    {
      relationship: "",
      diseaseCategory: "",
      diseaseSpecification: "",
      ageRange: "",
      maternalPaternal: "",
      relativeAvailable: "",
    },
  ]);
  const [formData, setFormData] = useState({
    ethnicity: "",
    city: "",
    state: "",
    country: "",
    physicianName: "",
    facilityName: "",
    facilityAddress: "",
    facilityCountry: "",
    facilityEmail: "",
    facilityPhone: "",
    additionalPhysicianName: "",
    additionalFacilityName: "",
    additionalFacilityEmail: "",
    additionalFacilityPhone: "",
    stage: "",
    diagnosis: "",
    diseaseAtTime: "",
    dateOfDiagnosis: { month: "", date: "", year: "" },
    treatments: [],
    transplant: "",
    hematologicalCancer: "",
    radiationTherapy: "",
    boneMarrowTransplant: "",
    geneticMutations: "",
    geneticMutationFamilyMember: "",
    familyHistoryCancer: "",
    gestationMonths: "",
    specimenType: "",
    currentGestationWeeks: "",
    certify: false,
    testName: "",
    testPrice: "",
  });
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: { month: "", date: "", year: "" },
    gender: "",
    address: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isTestLoading, setIsTestLoading] = useState(true);
  const [consentModalOpened, setConsentModalOpened] = useState(false);
  const [consentSubmitted, setConsentSubmitted] = useState(false);
  const [consentModalMode, setConsentModalMode] = useState("new"); // "new" or "edit"
  const [existingConsentData, setExistingConsentData] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [testError, setTestError] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isAdditionalPhysicianOpen, setIsAdditionalPhysicianOpen] =
    useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [showGeneralError, setShowGeneralError] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);

  const API_ENDPOINT = "/wp-json/dbsb/v1/orders";
  const UPDATE_ORDER_API_ENDPOINT = order_id ? `/wp-json/dbsb/v1/orders/${order_id}/update` : null;
  const ORDER_DETAILS_API_ENDPOINT = order_id ? `/wp-json/dbsb/v1/orders/${order_id}` : null;
  const CONSENT_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms/submit";
  const CONSENT_CHECK_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms/check/patient";
  const CONSENT_GET_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms/user/patient";
  const CONSENT_UPDATE_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms";
  const USER_API_ENDPOINT = "/wp-json/dbsb/v1/user/me";
  const TEST_API_ENDPOINT = "/wp-json/dbsb/v1/medical-test";

  useEffect(() => {
    // Map global context role to form role
    const roleFromContext = globalUserData.role === "patient" ? "individual" : "provider";
    setUserRole(roleFromContext);

    const fetchUserDetails = async () => {
      try {
        const response = await fetch(USER_API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings?.nonce || "",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch user details");
        const userData = await response.json();
        setCurrentUserId(userData.id);
        // Parse date_of_birth if valid, support both DD/MM/YYYY and YYYY-MM-DD
        let dob = { month: "", date: "", year: "" };
        if (userData.date_of_birth && typeof userData.date_of_birth === "string") {
          const dobStr = userData.date_of_birth.trim();
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(dobStr)) {
            // DD/MM/YYYY
            const [day, month, year] = dobStr.split("/");
            dob = {
              month: month ? parseInt(month, 10) : "",
              date: day || "",
              year: year || "",
            };
          } else if (/^\d{4}-\d{2}-\d{2}$/.test(dobStr)) {
            // YYYY-MM-DD
            const [year, month, day] = dobStr.split("-");
            dob = {
              month: month ? parseInt(month, 10) : "",
              date: day || "",
              year: year || "",
            };
          }
        }
        setUserData({
          firstName: userData.first_name || "",
          lastName: userData.last_name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          dateOfBirth: dob,
          gender: userData.gender || "",
          address: "", // Address is now fillable, so initialize as empty
        });
      } catch (error) {
        console.error("Error fetching user details:", error);
        setUserRole("individual");
      }
    };
    fetchUserDetails();
  }, [globalUserData.role]);

  useEffect(() => {
    const fetchTestDetails = async () => {
      // If order_id is provided but test_slug is not, fetch order details first
      if (order_id && !test_slug && ORDER_DETAILS_API_ENDPOINT) {
        setIsTestLoading(true);
        setTestError(null);
        try {
          if (!window.wpApiSettings?.nonce) {
            throw new Error(
              "Authentication token not found. Please try logging in again."
            );
          }

          const orderResponse = await fetch(ORDER_DETAILS_API_ENDPOINT, {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
          });

          if (!orderResponse.ok) {
            throw new Error("Failed to fetch order details.");
          }

          const orderData = await orderResponse.json();
          const orderTestSlug = orderData.test?.slug;

          if (orderTestSlug) {
            // Fetch test details using the slug from order
            const testResponse = await fetch(
              `${TEST_API_ENDPOINT}?test_slug=${orderTestSlug}`,
              {
                method: "GET",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  "X-WP-Nonce": window.wpApiSettings.nonce,
                },
              }
            );

            if (!testResponse.ok) {
              throw new Error("Failed to fetch test details.");
            }

            const testData = await testResponse.json();
            setFormData((prev) => ({
              ...prev,
              testName: testData.title || "No test selected",
              testPrice: testData.test_price ? testData.test_price.toString() : "No test selected",
            }));
            setCategory(testData.test_type || "oncology");
            setIsTestLoading(false);
            return;
          } else {
            throw new Error("Test slug not found in order details.");
          }
        } catch (error) {
          console.error("Error fetching order/test details:", error);
          setTestError(error.message);
          setFormData((prev) => ({
            ...prev,
            testName: "No test selected",
            testPrice: "No test selected",
          }));
          setIsTestLoading(false);
          return;
        }
      }

      if (!test_slug) {
        setTestError("No test selected.");
        setFormData((prev) => ({
          ...prev,
          testName: "No test selected",
          testPrice: "No test selected",
        }));
        setIsTestLoading(false);
        return;
      }

      setIsTestLoading(true);
      setTestError(null);

      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(
          `${TEST_API_ENDPOINT}?test_slug=${test_slug}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Test not found.");
          } else if (response.status === 401) {
            throw new Error("You must be logged in to view test details.");
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view this test.");
          }
          throw new Error("Failed to fetch test details.");
        }

        const data = await response.json();
        setFormData((prev) => ({
          ...prev,
          testName: data.title || "No test selected",
          testPrice: data.test_price ? data.test_price.toString() : "No test selected",
        }));
        setCategory(data.test_type || "oncology");
        setIsTestLoading(false);
      } catch (error) {
        console.error("Error fetching test details:", error);
        setTestError(error.message);
        setFormData((prev) => ({
          ...prev,
          testName: "No test selected",
          testPrice: "No test selected",
        }));
        setIsTestLoading(false);
      }
    };
    fetchTestDetails();
  }, [test_slug, order_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name in userData) {
      setUserData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
      if (hasAttemptedSubmit) {
        setTouched((prev) => ({ ...prev, [name]: true }));
        validateForm();
      }
    } else {
      updateForm(name, type === "checkbox" ? checked : value);
      if (hasAttemptedSubmit) {
        setTouched((prev) => ({ ...prev, [name]: true }));
        validateForm();
      }
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateForm();
  };

  const handlePhoneChange = (value, field) => {
    if (field in userData) {
      setUserData((prev) => ({ ...prev, [field]: value || "" }));
    } else {
      updateForm(field, value || "");
    }
  };

  const handlePhoneBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateForm();
  };

  const handleDateChange = (field, value) => {
    let sanitizedValue = value;
    if (field === "date")
      sanitizedValue = value.slice(0, 2).replace(/[^0-9]/g, "");
    else if (field === "year")
      sanitizedValue = value.slice(0, 4).replace(/[^0-9]/g, "");
    updateForm("dateOfDiagnosis", {
      ...formData.dateOfDiagnosis,
      [field]: sanitizedValue,
    });
  };

  const handleDateBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateForm();
  };

  // const handlePersonalCancerChange = (index, field, value) => {
  //   const updatedHistory = [...personalCancerHistory];
  //   updatedHistory[index][field] = value;
  //   if (field === "cancerType" && value === "") {
  //     updatedHistory[index].cancerTypeSpec = "";
  //     updatedHistory[index].age = "";
  //   }
  //   if (field === "cancerTypeSpec" && value === "") {
  //     updatedHistory[index].age = "";
  //   }
  //   setPersonalCancerHistory(updatedHistory);
  // };

  const handleFamilyCancerChange = (index, field, value) => {
    const updatedHistory = [...familyCancerHistory];
    updatedHistory[index][field] = value;
    if (field === "cancerType" && value === "") {
      updatedHistory[index].cancerTypeSpec = "";
    }
    setFamilyCancerHistory(updatedHistory);
  };

  const handleFamilyDiseaseChange = (index, field, value) => {
    const updatedHistory = [...familyDiseaseHistory];
    updatedHistory[index][field] = value;
    if (field === "diseaseCategory" && value === "") {
      updatedHistory[index].diseaseSpecification = "";
    }
    setFamilyDiseaseHistory(updatedHistory);
  };

  const handleOncologyDiagnosisChange = (index, field, value) => {
    const updated = [...oncologyDiagnosisHistory];
    updated[index][field] = value;
    if (field === "cancerType" && value === "") {
      updated[index].cancerTypeSpec = "";
    }
    setOncologyDiagnosisHistory(updated);
    if (hasAttemptedSubmit) {
      setTouched((prev) => ({
        ...prev,
        [`${field === "cancerType" ? "cancerType" : field === "cancerTypeSpec" ? "cancerTypeSpec" : "age"}_${index}`]: true,
      }));
      validateForm();
    }
  };

  const addOncologyDiagnosisEntry = () => {
    setOncologyDiagnosisHistory([
      ...oncologyDiagnosisHistory,
      { cancerType: "", cancerTypeSpec: "", age: "" },
    ]);
  };

  const removeOncologyDiagnosisEntry = (index) => {
    setOncologyDiagnosisHistory(
      oncologyDiagnosisHistory.filter((_, i) => i !== index)
    );
    setTouched((prev) => {
      const newTouched = { ...prev };
      delete newTouched[`cancerType_${index}`];
      delete newTouched[`cancerTypeSpec_${index}`];
      delete newTouched[`age_${index}`];
      const updatedTouched = {};
      Object.keys(newTouched).forEach((key) => {
        const match = key.match(/^(cancerType|cancerTypeSpec|age)_(\d+)$/);
        if (match) {
          const field = match[1];
          const idx = parseInt(match[2], 10);
          if (idx > index) {
            updatedTouched[`${field}_${idx - 1}`] = newTouched[key];
          } else if (idx < index) {
            updatedTouched[key] = newTouched[key];
          }
        } else {
          updatedTouched[key] = newTouched[key];
        }
      });
      return updatedTouched;
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`cancerType_${index}`];
      delete newErrors[`cancerTypeSpec_${index}`];
      delete newErrors[`age_${index}`];
      const updatedErrors = {};
      Object.keys(newErrors).forEach((key) => {
        const match = key.match(/^(cancerType|cancerTypeSpec|age)_(\d+)$/);
        if (match) {
          const field = match[1];
          const idx = parseInt(match[2], 10);
          if (idx > index) {
            updatedErrors[`${field}_${idx - 1}`] = newErrors[key];
          } else if (idx < index) {
            updatedErrors[key] = newErrors[key];
          }
        } else {
          updatedErrors[key] = newErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  // const addPersonalCancerEntry = () => {
  //   setPersonalCancerHistory([
  //     ...personalCancerHistory,
  //     { cancerType: "", cancerTypeSpec: "", age: "" },
  //   ]);
  // };

  const handleHereditaryDiagnosisChange = (index, field, value) => {
    const updated = [...hereditaryDiagnosisHistory];
    updated[index][field] = value;
    if (field === "cancerType" && value === "") {
      updated[index].cancerTypeSpec = "";
    }
    setHereditaryDiagnosisHistory(updated);
    if (hasAttemptedSubmit) {
      setTouched((prev) => ({
        ...prev,
        [`hereditary_${field === "cancerType" ? "cancerType" : field === "cancerTypeSpec" ? "cancerTypeSpec" : "age"}_${index}`]: true,
      }));
      validateForm();
    }
  };

  const addHereditaryDiagnosisEntry = () => {
    setHereditaryDiagnosisHistory([
      ...hereditaryDiagnosisHistory,
      { cancerType: "", cancerTypeSpec: "", age: "" },
    ]);
  };

  const removeHereditaryDiagnosisEntry = (index) => {
    setHereditaryDiagnosisHistory(
      hereditaryDiagnosisHistory.filter((_, i) => i !== index)
    );
    setTouched((prev) => {
      const newTouched = { ...prev };
      delete newTouched[`hereditary_cancerType_${index}`];
      delete newTouched[`hereditary_cancerTypeSpec_${index}`];
      delete newTouched[`hereditary_age_${index}`];
      const updatedTouched = {};
      Object.keys(newTouched).forEach((key) => {
        const match = key.match(/^hereditary_(cancerType|cancerTypeSpec|age)_(\d+)$/);
        if (match) {
          const field = match[1];
          const idx = parseInt(match[2], 10);
          if (idx > index) {
            updatedTouched[`hereditary_${field}_${idx - 1}`] = newTouched[key];
          } else if (idx < index) {
            updatedTouched[key] = newTouched[key];
          }
        } else {
          updatedTouched[key] = newTouched[key];
        }
      });
      return updatedTouched;
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`hereditary_cancerType_${index}`];
      delete newErrors[`hereditary_cancerTypeSpec_${index}`];
      delete newErrors[`hereditary_age_${index}`];
      const updatedErrors = {};
      Object.keys(newErrors).forEach((key) => {
        const match = key.match(/^hereditary_(cancerType|cancerTypeSpec|age)_(\d+)$/);
        if (match) {
          const field = match[1];
          const idx = parseInt(match[2], 10);
          if (idx > index) {
            updatedErrors[`hereditary_${field}_${idx - 1}`] = newErrors[key];
          } else if (idx < index) {
            updatedErrors[key] = newErrors[key];
          }
        } else {
          updatedErrors[key] = newErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  // const removePersonalCancerEntry = (index) => {
  //   setPersonalCancerHistory(
  //     personalCancerHistory.filter((_, i) => i !== index)
  //   );
  //   setTouched((prev) => {
  //     const newTouched = { ...prev };
  //     delete newTouched[`cancerType_${index}`];
  //     delete newTouched[`cancerTypeSpec_${index}`];
  //     delete newTouched[`age_${index}`];
  //     const updatedTouched = {};
  //     Object.keys(newTouched).forEach((key) => {
  //         const match = key.match(/^(cancerType|cancerTypeSpec|age)_(\d+)$/);
  //         if (match) {
  //           const field = match[1];
  //           const idx = parseInt(match[2], 10);
  //           if (idx > index) {
  //             updatedTouched[`${field}_${idx - 1}`] = newTouched[key];
  //           } else if (idx < index) {
  //             updatedTouched[key] = newTouched[key];
  //           }
  //         } else {
  //           updatedErrors[key] = newTouched[key];
  //         }
  //       });
  //       return updatedTouched;
  //     });
  //     setErrors((prev) => {
  //       const newErrors = { ...prev };
  //       delete newErrors[`cancerType_${index}`];
  //       delete newErrors[`cancerTypeSpec_${index}`];
  //       delete newErrors[`age_${index}`];
  //       const updatedErrors = {};
  //       Object.keys(newErrors).forEach((key) => {
  //         const match = key.match(/^(cancerType|cancerTypeSpec|age)_(\d+)$/);
  //         if (match) {
  //           const field = match[1];
  //           const idx = parseInt(match[2], 10);
  //           if (idx > index) {
  //             updatedErrors[`${field}_${idx - 1}`] = newErrors[key];
  //           } else if (idx < index) {
  //             updatedErrors[key] = newErrors[key];
  //           }
  //         } else {
  //           updatedErrors[key] = newErrors[key];
  //         }
  //       });
  //       return updatedErrors;
  //     });
  //   };

  const addFamilyCancerEntry = () => {
    setFamilyCancerHistory([
      ...familyCancerHistory,
      {
        relationship: "",
        cancerType: "",
        cancerTypeSpec: "",
        ageRange: "",
        maternalPaternal: "",
        relativeAvailable: "",
      },
    ]);
  };

  const addFamilyDiseaseEntry = () => {
    setFamilyDiseaseHistory([
      ...familyDiseaseHistory,
      {
        relationship: "",
        diseaseCategory: "",
        diseaseSpecification: "",
        ageRange: "",
        maternalPaternal: "",
        relativeAvailable: "",
      },
    ]);
  };

  const removeFamilyCancerEntry = (index) => {
    setFamilyCancerHistory(familyCancerHistory.filter((_, i) => i !== index));
    setTouched((prev) => {
      const newTouched = { ...prev };
      delete newTouched[`relationship_${index}`];
      delete newTouched[`familyCancerType_${index}`];
      delete newTouched[`familyCancerTypeSpec_${index}`];
      delete newTouched[`ageRange_${index}`];
      delete newTouched[`maternalPaternal_${index}`];
      delete newTouched[`relativeAvailable_${index}`];
      const updatedTouched = {};
      Object.keys(newTouched).forEach((key) => {
        const match = key.match(
          /^(relationship|familyCancerType|familyCancerTypeSpec|ageRange|maternalPaternal|relativeAvailable)_(\d+)$/
        );
        if (match) {
          const field = match[1];
          const idx = parseInt(match[2], 10);
          if (idx > index) {
            updatedTouched[`${field}_${idx - 1}`] = newTouched[key];
          } else if (idx < index) {
            updatedTouched[key] = newTouched[key];
          }
        } else {
          updatedTouched[key] = newTouched[key];
        }
      });
      return updatedTouched;
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`relationship_${index}`];
      delete newErrors[`familyCancerType_${index}`];
      delete newErrors[`familyCancerTypeSpec_${index}`];
      delete newErrors[`ageRange_${index}`];
      delete newErrors[`maternalPaternal_${index}`];
      delete newErrors[`relativeAvailable_${index}`];
      const updatedErrors = {};
      Object.keys(newErrors).forEach((key) => {
        const match = key.match(
          /^(relationship|familyCancerType|familyCancerTypeSpec|ageRange|maternalPaternal|relativeAvailable)_(\d+)$/
        );
        if (match) {
          const field = match[1];
          const idx = parseInt(match[2], 10);
          if (idx > index) {
            updatedErrors[`${field}_${idx - 1}`] = newErrors[key];
          } else if (idx < index) {
            updatedErrors[key] = newErrors[key];
          }
        } else {
          updatedErrors[key] = newErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const removeFamilyDiseaseEntry = (index) => {
    setFamilyDiseaseHistory(
      familyDiseaseHistory.filter((_, i) => i !== index)
    );
    setTouched((prev) => {
      const newTouched = { ...prev };
      delete newTouched[`diseaseRelationship_${index}`];
      delete newTouched[`diseaseCategory_${index}`];
      delete newTouched[`diseaseSpecification_${index}`];
      delete newTouched[`diseaseAgeRange_${index}`];
      delete newTouched[`diseaseMaternalPaternal_${index}`];
      delete newTouched[`diseaseRelativeAvailable_${index}`];
      const updatedTouched = {};
      Object.keys(newTouched).forEach((key) => {
        const match = key.match(
          /^(diseaseRelationship|diseaseCategory|diseaseSpecification|diseaseAgeRange|diseaseMaternalPaternal|diseaseRelativeAvailable)_(\d+)$/
        );
        if (match) {
          const field = match[1];
          const idx = parseInt(match[2], 10);
          if (idx > index) {
            updatedTouched[`${field}_${idx - 1}`] = newTouched[key];
          } else if (idx < index) {
            updatedTouched[key] = newTouched[key];
          }
        } else {
          updatedTouched[key] = newTouched[key];
        }
      });
      return updatedTouched;
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`diseaseRelationship_${index}`];
      delete newErrors[`diseaseCategory_${index}`];
      delete newErrors[`diseaseSpecification_${index}`];
      delete newErrors[`diseaseAgeRange_${index}`];
      delete newErrors[`diseaseMaternalPaternal_${index}`];
      delete newErrors[`diseaseRelativeAvailable_${index}`];
      const updatedErrors = {};
      Object.keys(newErrors).forEach((key) => {
        const match = key.match(
          /^(diseaseRelationship|diseaseCategory|diseaseSpecification|diseaseAgeRange|diseaseMaternalPaternal|diseaseRelativeAvailable)_(\d+)$/
        );
        if (match) {
          const field = match[1];
          const idx = parseInt(match[2], 10);
          if (idx > index) {
            updatedErrors[`${field}_${idx - 1}`] = newErrors[key];
          } else if (idx < index) {
            updatedErrors[key] = newErrors[key];
          }
        } else {
          updatedErrors[key] = newErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const updateForm = (name, value) => {
    if (name === "dateOfDiagnosis") {
      setFormData((prev) => ({ ...prev, dateOfDiagnosis: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Validate all fields irrespective of touched (used on submit)
  // Only required fields: Patient info, Other patient info, Consent form, and gestation age for prenatal
  const validateAllOnSubmit = () => {
    const newErrors = {};
    const currentYear = new Date().getFullYear();

    // Other patient information section - always required
    if (!formData.ethnicity) newErrors.ethnicity = "Ethnicity is required";
    if (!formData.city) newErrors.city = "City is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.country) newErrors.country = "Country is required";
    if (!userData.address) newErrors.address = "Street address is required";

    // Gestation age is required only for prenatal category
    if (category === "prenatal") {
      if (formData.currentGestationWeeks === "" || formData.currentGestationWeeks === undefined)
        newErrors.currentGestationWeeks = "Gestation age is required";
      else if (isNaN(Number(formData.currentGestationWeeks)) || Number(formData.currentGestationWeeks) < 0 || Number(formData.currentGestationWeeks) > 45)
        newErrors.currentGestationWeeks = "Enter a valid number of weeks";
    }

    // Consent form - always required
    if (!consentSubmitted)
      newErrors.consent = "You must complete the consent form";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const newErrors = {};
    const currentYear = new Date().getFullYear();

    // If form has been attempted to submit, validate all required fields
    const shouldValidateAll = hasAttemptedSubmit;

    // Other patient information section - always required
    if ((shouldValidateAll || touched.ethnicity) && !formData.ethnicity)
      newErrors.ethnicity = "Ethnicity is required";
    if ((shouldValidateAll || touched.city) && !formData.city) newErrors.city = "City is required";
    if ((shouldValidateAll || touched.state) && !formData.state) newErrors.state = "State is required";
    if ((shouldValidateAll || touched.country) && !formData.country)
      newErrors.country = "Country is required";
    if ((shouldValidateAll || touched.address) && !userData.address)
      newErrors.address = "Street address is required";

    // Gestation age is required only for prenatal category (only validate when touched or on submit)
    if (category === "prenatal") {
      if ((shouldValidateAll || touched.currentGestationWeeks) && (formData.currentGestationWeeks === "" || formData.currentGestationWeeks === undefined))
        newErrors.currentGestationWeeks = "Gestation age is required";
      else if ((shouldValidateAll || touched.currentGestationWeeks) && (isNaN(Number(formData.currentGestationWeeks)) || Number(formData.currentGestationWeeks) < 0 || Number(formData.currentGestationWeeks) > 45))
        newErrors.currentGestationWeeks = "Enter a valid number of weeks";
    }

    // Consent form - always required
    if (!consentSubmitted)
      newErrors.consent = "You must complete the consent form";

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const isFormIncomplete = () => {
    const requiredFields = {
      // Other patient information section - always required
      ethnicity: formData.ethnicity,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      address: userData.address,
      // Consent form - always required
      consent: consentSubmitted,
    };

    // Gestation age is required only for prenatal category
    if (category === "prenatal") {
      requiredFields.currentGestationWeeks = formData.currentGestationWeeks !== "" && formData.currentGestationWeeks !== undefined;
    }

    return Object.values(requiredFields).some(
      (value) => value === "" || value === false || value === undefined
    );
  };

  // Check if form has errors or is incomplete
  const isFormInvalid = () => {
    // Check if there are any errors
    const hasErrors = Object.keys(errors).length > 0 && 
      Object.values(errors).some((error) => error !== "" && error !== undefined);
    
    // Check if required fields are incomplete
    // Other patient information section - always required
    const missingRequiredFields = 
      !formData.ethnicity || 
      !formData.city || 
      !formData.state || 
      !formData.country || 
      !userData.address ||
      !consentSubmitted;
    
    // Gestation age is required only for prenatal category
    const missingPrenatalField = category === "prenatal" && 
      (formData.currentGestationWeeks === "" || formData.currentGestationWeeks === undefined);
    
    return hasErrors || missingRequiredFields || missingPrenatalField;
  };

  // useEffect(() => {
  //   validateForm();
  // }, [formData, touched, personalCancerHistory, familyCancerHistory, userData]);

  useEffect(() => {
    validateForm();
  }, [formData, touched, familyCancerHistory, familyDiseaseHistory, oncologyDiagnosisHistory, userData, consentSubmitted]);

  // Handle 3-second auto-hide for general error message
  useEffect(() => {
    if (showGeneralError) {
      const timer = setTimeout(() => {
        setShowGeneralError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showGeneralError]);

  // Check for existing consent form
  useEffect(() => {
    const checkExistingConsent = async () => {
      try {
        if (!window.wpApiSettings?.nonce) {
          return;
        }

        const response = await fetch(CONSENT_CHECK_API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.has_consent) {
            // Get the full consent form data
            const consentResponse = await fetch(CONSENT_GET_API_ENDPOINT, {
              method: "GET",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": window.wpApiSettings.nonce,
              },
            });

            if (consentResponse.ok) {
              const consentData = await consentResponse.json();
              setExistingConsentData(consentData.consent_form);
              setConsentSubmitted(true);
            }
          }
        }
      } catch (error) {
        console.error("Error checking existing consent:", error);
      }
    };

    checkExistingConsent();
  }, []);

  const handleConsentSubmit = async (consentData) => {
    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found. Please try logging in again.");
      }

      const isUpdate = consentData.id;
      const endpoint = isUpdate ? `${CONSENT_UPDATE_API_ENDPOINT}/${consentData.id}` : CONSENT_API_ENDPOINT;
      const method = isUpdate ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method: method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        body: JSON.stringify(consentData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || "Failed to submit consent form.");
      }

      // If this was a new submission, capture the created consent for immediate edit prefill
      if (!isUpdate) {
        try {
          const created = await response.json().catch(() => null);
          if (created) {
            setExistingConsentData(created.consent_form || created);
          }
        } catch (parseError) {
          // Non-fatal if server didn't return JSON body
          console.warn("Could not parse created consent response:", parseError);
        }
      }

      // If this was an update, refresh the existing consent data
      if (isUpdate) {
        try {
          const refreshResponse = await fetch(CONSENT_GET_API_ENDPOINT, {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setExistingConsentData(refreshData.consent_form);
          }
        } catch (refreshError) {
          console.error("Error refreshing consent data:", refreshError);
          // Don't fail the main operation if refresh fails
        }
      }

      setConsentSubmitted(true);
      setConsentModalOpened(false);
      setConsentModalMode("new");

      // Trigger immediate validation to enable the continue button
      setTimeout(() => {
        validateForm();
      }, 0);
    } catch (error) {
      console.error("Error submitting consent form:", error);
      setApiError(error.message || "Failed to submit consent form. Please try again.");
    }
  };

  const handleEditConsent = () => {
    setConsentModalMode("edit");
    setConsentModalOpened(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    setSubmissionSuccess(false);
    setTouched({
      // Other patient information section - always required
      ethnicity: true,
      city: true,
      state: true,
      country: true,
      address: true,
      // Gestation age is required only for prenatal category
      ...(category === "prenatal" && { currentGestationWeeks: true }),
    });

    // On submit, validate all required fields immediately
    // If validation fails, show general error and return without proceeding to submission
    if (!validateAllOnSubmit()) {
      setShowGeneralError(true);
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const dobFormatted = formData.dateOfDiagnosis
        ? `${formData.dateOfDiagnosis.year}-${String(
            formData.dateOfDiagnosis.month
          ).padStart(2, "0")}-${String(formData.dateOfDiagnosis.date).padStart(
            2,
            "0"
          )}`
        : "";

      if (!window.wpApiSettings?.nonce) {
        throw new Error(
          "Authentication token not found. Please try logging in again."
        );
      }

      const requisitionFormData = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phone: userData.phone,
        dateOfBirth: userData.dateOfBirth,
        gender: userData.gender,
        address: userData.address,
        ethnicity: formData.ethnicity,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        ...(category === "oncology" || category === "prenatal" || category === "hereditary_diseases") && {
        treatingPhysician: {
          name: formData.physicianName,
          facilityName: formData.facilityName,
          facilityAddress: formData.facilityAddress,
          facilityCountry: formData.facilityCountry,
          facilityEmail: formData.facilityEmail,
          facilityPhone: formData.facilityPhone,
        },
        },
        ...(category === "oncology" || category === "prenatal" || category === "hereditary_diseases") && {
        additionalPhysician: isAdditionalPhysicianOpen
          ? {
              name: formData.additionalPhysicianName,
              facilityName: formData.additionalFacilityName,
              facilityEmail: formData.additionalFacilityEmail,
              facilityPhone: formData.additionalFacilityPhone,
            }
          : null,
        },
        ...(category === "oncology" && {
          stage: formData.stage,
          diagnosis: oncologyDiagnosisHistory,
          diseaseAtTime: formData.diseaseAtTime,
          dateOfDiagnosis: dobFormatted,
          treatments: formData.treatments,
          transplant: formData.transplant,
        }),
        ...(category === "hereditary_cancer" && {
          hematologicalCancer: formData.hematologicalCancer,
          radiationTherapy: formData.radiationTherapy,
          boneMarrowTransplant: formData.boneMarrowTransplant,
          geneticMutations: formData.geneticMutations,
          geneticMutationFamilyMember: formData.geneticMutationFamilyMember,
          familyHistoryCancer: formData.familyHistoryCancer,
          ...(formData.familyHistoryCancer === "yes" && {
            familyCancerHistory: familyCancerHistory,
          }),
        }),
        ...(category === "hereditary_diseases" && {
          familyDiseaseHistory: familyDiseaseHistory,
        }),
        ...(category === "prenatal" && {
          specimenType: formData.specimenType,
          currentGestationWeeks: formData.currentGestationWeeks,
        }),
      };

      // If order_id exists, update the order; otherwise create a new one (for backward compatibility)
      if (order_id && UPDATE_ORDER_API_ENDPOINT) {
        // Update existing order
        const updateData = {
          collection_method: "lab_visit",
          requisition_type: "online",
          online_requisition_data: JSON.stringify(requisitionFormData),
          notes: "",
        };

        const response = await fetch(UPDATE_ORDER_API_ENDPOINT, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
          body: JSON.stringify(updateData),
        });

        const data = await response.json();

        if (!response.ok) {
          switch (response.status) {
            case 401:
              throw new Error(
                "You must be logged in to submit the form. Please log in and try again."
              );
            case 403:
              throw new Error("You don't have permission to submit this form.");
            case 404:
              throw new Error("Order not found.");
            default:
              throw new Error(data.message || "Failed to update order.");
          }
        }

        setSubmissionSuccess(true);
        setTimeout(() => {
          navigate(`/my-order/${order_id}`, {
            state: { refresh: true, timestamp: Date.now() },
          });
        }, 3000);
      } else {
        // Create new order (backward compatibility)
        const orderData = {
          order_type: userRole,
          test_slug: test_slug,
          patient_user_id: userRole === "individual" ? currentUserId : null,
          health_provider_user_id: userRole === "provider" ? currentUserId : null,
          patient_post_id: userRole === "provider" ? providerPatientId : null,
          collection_method: "lab_visit",
          order_date: new Date().toISOString().split("T")[0],
          requisition_type: "online",
          online_requisition_data: JSON.stringify(requisitionFormData),
          notes: "",
        };

        const response = await fetch(API_ENDPOINT, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
          body: JSON.stringify(orderData),
        });

        const data = await response.json();

        if (!response.ok) {
          switch (response.status) {
            case 401:
              throw new Error(
                "You must be logged in to submit the form. Please log in and try again."
              );
            case 403:
              throw new Error("You don't have permission to submit this form.");
            default:
              throw new Error(data.message || "Failed to create order.");
          }
        }

        setSubmissionSuccess(true);
        setTimeout(() => {
          navigate(
            `/review-test-order?order_id=${data.order_id}&test_slug=${data.test_slug}`
          );
        }, 3000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setApiError(error.message || "Failed to submit form. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptionSelect = (option) => {
    setShowUploadForm(option === "upload");
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  const sectionVariants = {
    hidden: { height: 0, opacity: 0, y: -20, overflow: "hidden" },
    visible: {
      height: "auto",
      opacity: 1,
      y: 0,
      overflow: "visible",
      transition: { duration: 0.3, ease: "easeInOut" },
    },
    exit: {
      height: 0,
      opacity: 0,
      y: -20,
      overflow: "hidden",
      transition: { duration: 0.3, ease: "easeInOut" },
    },
  };

  if (isTestLoading) {
    return (
      <div className="patient-requisition-container">
        <Loading />
      </div>
    );
  }

  if (testError) {
    return (
      <div className="patient-requisition-container">
        <motion.div
          className="error-message"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {testError}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="patient-requisition-container">
      {apiError && <div className="error-message">{apiError}</div>}

      {userRole === "individual" ? (
        <motion.div
          className="requisition-form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* <OrderPopup onOptionSelect={handleOptionSelect} /> */}

          <motion.div
            className="patient-requisition-content"
            variants={formVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="content-wrapper">
              <motion.div
                className="order-summary-card-wrapper"
                variants={itemVariants}
              >
                <OrderSummaryCard orderId={order_id} />
              </motion.div>

              <div className="card-error">
                {showUploadForm ? (
                  <UploadRequisitionForm />
                ) : (
                  <motion.div
                    className="patient-requisition-card"
                    variants={itemVariants}
                  >
                    <motion.form
                      onSubmit={handleSubmit}
                      className="patient-requisition-form"
                      variants={formVariants}
                    >
                      {/* Patient Information */}
                      <motion.div
                        variants={itemVariants}
                        className="form-group"
                      >
                        <h3>Patient Information</h3>
                        <div className="form-group name-row">
                          <div className="name-input">
                            <label>First Name</label>
                            <input
                              name="firstName"
                              value={userData.firstName}
                              disabled
                            />
                          </div>
                          <div className="name-input">
                            <label>Last Name</label>
                            <input
                              name="lastName"
                              value={userData.lastName}
                              disabled
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            name="email"
                            value={userData.email}
                            disabled
                          />
                        </div>
                        <div className="form-group phone">
                          <label>Phone Number</label>
                          <PhoneInput
                            international
                            countryCallingCodeEditable={false}
                            defaultCountry="NG"
                            value={userData.phone}
                            disabled
                          />
                        </div>
                        <div className="form-group">
                          <label>Gender</label>
                          <select
                            name="gender"
                            value={userData.gender}
                            disabled
                          >
                            <option value="">Select from dropdown</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div className="form-group dob">
                          <label>Date of Birth</label>
                          <div className="dob-inputs">
                            <div className="dob-input">
                              <select
                                name="month"
                                value={userData.dateOfBirth.month}
                                disabled
                              >
                                <option value="">Month</option>
                                {Array.from({ length: 12 }, (_, i) => (
                                  <option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString("default", {
                                      month: "long",
                                    })}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="dob-input">
                              <input
                                type="text"
                                name="date"
                                value={userData.dateOfBirth.date}
                                placeholder="Day"
                                maxLength="2"
                                disabled
                                style={{
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                }}
                              />
                            </div>
                            <div className="dob-input">
                              <input
                                type="text"
                                name="year"
                                value={userData.dateOfBirth.year}
                                placeholder="Year"
                                maxLength="4"
                                disabled
                                style={{
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>

                      {/* Other Patient Information */}
                      <motion.div
                        variants={itemVariants}
                        className="form-group"
                      >
                        <h3>Other patient information</h3>
                        <div className="form-group">
                          <label>Ethnicity</label>
                          <input
                            name="ethnicity"
                            value={formData.ethnicity}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter text here"
                          />
                          {errors.ethnicity && (
                            <span className="error">{errors.ethnicity}</span>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Street Address</label>
                          <textarea
                            name="address"
                            value={userData.address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            rows="4"
                            placeholder="Enter street address here"
                          />
                          {errors.address && (
                            <span className="error">{errors.address}</span>
                          )}
                        </div>
                        <div className="form-group">
                          <label>City</label>
                          <input
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter city here"
                          />
                          {errors.city && (
                            <span className="error">{errors.city}</span>
                          )}
                        </div>
                        <div className="form-group">
                          <label>State</label>
                          <input
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter state here"
                          />
                          {errors.state && (
                            <span className="error">{errors.state}</span>
                          )}
                        </div>
                        <div className="form-group">
                          <label>Country</label>
                          <input
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter country here"
                          />
                          {errors.country && (
                            <span className="error">{errors.country}</span>
                          )}
                        </div>
                      </motion.div>

                      {/* Current Diagnosis and Patient History */}
                      {(category === "oncology" ||
                        category === "hereditary_cancer" || category === "hereditary_diseases") && (
                        <CurrentDiagnosis
                          category={category}
                          formData={formData}
                          errors={errors}
                          touched={touched}
                          // personalCancerHistory={personalCancerHistory}
                          oncologyDiagnosisHistory={oncologyDiagnosisHistory}
                          hereditaryDiagnosisHistory={hereditaryDiagnosisHistory}
                          familyCancerHistory={familyCancerHistory}
                          familyDiseaseHistory={familyDiseaseHistory}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          handleDateChange={handleDateChange}
                          handleDateBlur={handleDateBlur}
                          // handlePersonalCancerChange={
                          //   handlePersonalCancerChange
                          // }
                          handleFamilyCancerChange={handleFamilyCancerChange}
                          handleFamilyDiseaseChange={handleFamilyDiseaseChange}
                          handleOncologyDiagnosisChange={handleOncologyDiagnosisChange}
                          handleHereditaryDiagnosisChange={handleHereditaryDiagnosisChange}
                          // addPersonalCancerEntry={addPersonalCancerEntry}
                          // removePersonalCancerEntry={removePersonalCancerEntry}
                          addOncologyDiagnosisEntry={addOncologyDiagnosisEntry}
                          removeOncologyDiagnosisEntry={removeOncologyDiagnosisEntry}
                          addHereditaryDiagnosisEntry={addHereditaryDiagnosisEntry}
                          removeHereditaryDiagnosisEntry={removeHereditaryDiagnosisEntry}
                          addFamilyCancerEntry={addFamilyCancerEntry}
                          removeFamilyCancerEntry={removeFamilyCancerEntry}
                          addFamilyDiseaseEntry={addFamilyDiseaseEntry}
                          removeFamilyDiseaseEntry={removeFamilyDiseaseEntry}
                          updateForm={updateForm}
                          setTouched={setTouched}
                        />
                      )}

                      {category === "prenatal" && (
                        <motion.div
                          variants={itemVariants}
                          className="form-group"
                        >
                          <h3>Specimen Information</h3>
                          <div className="form-group">
                            <label>Specimen type</label>
                            <select
                              name="specimenType"
                              value={formData.specimenType}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="">Select from dropdown</option>
                              <option value="Blood">Blood</option>
                            </select>
                            {errors.specimenType && (
                              <span className="error">{errors.specimenType}</span>
                            )}
                          </div>
                          <div className="form-group">
                            <label>Current gestation age (in weeks)</label>
                            <input
                              type="number"
                              name="currentGestationWeeks"
                              value={formData.currentGestationWeeks}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter number of weeks"
                              min="0"
                              max="45"
                              style={{ WebkitAppearance: "none", MozAppearance: "textfield" }}
                            />
                            {errors.currentGestationWeeks && (
                              <span className="error">{errors.currentGestationWeeks}</span>
                            )}
                          </div>
                        </motion.div>
                      )}

                      {/* Treating Physician Information */}
                      {(category === "oncology" || category === "prenatal" || category === "hereditary_diseases") && (
                        <motion.div
                          variants={itemVariants}
                          className="form-group"
                        >
                          <h3>Treating physician information</h3>
                          <div className="form-group">
                            <label>Name</label>
                            <input
                              name="physicianName"
                              value={formData.physicianName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter full name here"
                            />
                            {errors.physicianName && (
                              <span className="error">
                                {errors.physicianName}
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label>Facility name</label>
                            <input
                              name="facilityName"
                              value={formData.facilityName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter facility name here"
                            />
                            {errors.facilityName && (
                              <span className="error">{errors.facilityName}</span>
                            )}
                          </div>
                          <div className="form-group">
                            <label>Facility address</label>
                            <input
                              name="facilityAddress"
                              value={formData.facilityAddress}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter facility address here"
                            />
                            {errors.facilityAddress && (
                              <span className="error">
                                {errors.facilityAddress}
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label>Country</label>
                            <input
                              name="facilityCountry"
                              value={formData.facilityCountry}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter country here"
                            />
                            {errors.facilityCountry && (
                              <span className="error">
                                {errors.facilityCountry}
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label>Email</label>
                            <input
                              type="email"
                              name="facilityEmail"
                              value={formData.facilityEmail}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter email here"
                            />
                            {errors.facilityEmail && (
                              <span className="error">
                                {errors.facilityEmail}
                              </span>
                            )}
                          </div>
                          <div className="form-group phone">
                            <label>Phone number</label>
                            <PhoneInput
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="NG"
                              value={formData.facilityPhone}
                              onChange={(value) =>
                                handlePhoneChange(value, "facilityPhone")
                              }
                              onBlur={() => handlePhoneBlur("facilityPhone")}
                              placeholder="Enter phone number"
                              maxLength={17}
                            />
                            {errors.facilityPhone && (
                              <span className="error">
                                {errors.facilityPhone}
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label>Fax (optional)</label>
                            <input
                              name="facilityFax"
                              value={formData.facilityFax}
                              onChange={handleChange}
                              placeholder="Enter fax here"
                            />
                          </div>
                        </motion.div>
                      )}

                      {/* Additional Physician to be Copied (Collapsible) */}
                      {(category === "oncology" || category === "prenatal" || category === "hereditary_diseases") && (
                        <motion.div
                          variants={itemVariants}
                          className="form-group"
                        >
                          <div
                            className="collapsible-header"
                            onClick={() =>
                              setIsAdditionalPhysicianOpen(
                                !isAdditionalPhysicianOpen
                              )
                            }
                          >
                            <label>
                              Additional physician to be copied
                            </label>
                            {isAdditionalPhysicianOpen ? (
                              <ChevronUp className="toggle-icon" />
                            ) : (
                              <ChevronDown className="toggle-icon" />
                            )}
                          </div>
                          <AnimatePresence>
                            {isAdditionalPhysicianOpen && (
                              <motion.div
                                variants={sectionVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                              >
                                <div className="form-group">
                                  <label>Name</label>
                                  <input
                                    name="additionalPhysicianName"
                                    value={formData.additionalPhysicianName}
                                    onChange={handleChange}
                                    placeholder="Enter full name here"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Facility name</label>
                                  <input
                                    name="additionalFacilityName"
                                    value={formData.additionalFacilityName}
                                    onChange={handleChange}
                                    placeholder="Enter facility name here"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Email</label>
                                  <input
                                    type="email"
                                    name="additionalFacilityEmail"
                                    value={formData.additionalFacilityEmail}
                                    onChange={handleChange}
                                    placeholder="Enter email here"
                                  />
                                </div>
                                <div className="form-group phone">
                                  <label>Phone number</label>
                                  <PhoneInput
                                    international
                                    countryCallingCodeEditable={false}
                                    defaultCountry="NG"
                                    value={formData.additionalFacilityPhone}
                                    onChange={(value) =>
                                      handlePhoneChange(
                                        value,
                                        "additionalFacilityPhone"
                                      )
                                    }
                                    placeholder="Enter phone number"
                                    maxLength={17}
                                  />
                                </div>
                                <div className="form-group fax-form-group">
                                  <label>Fax</label>
                                  <input
                                    name="additionalFacilityFax"
                                    value={formData.additionalFacilityFax}
                                    onChange={handleChange}
                                    placeholder="Enter fax here"
                                  />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      )}

                      <div className="form-group test-name-group">
                        <label>Test Name</label>
                        <input
                          className="test-type-name"
                          name="testName"
                          value={formData.testName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Enter test name"
                          disabled
                        />
                        {errors.testName && (
                          <span className="error">{errors.testName}</span>
                        )}
                      </div>

                      {/* Consent Form Section */}
                      <motion.div
                        variants={itemVariants}
                        className="form-group"
                      >
                        {!consentSubmitted ? (
                          <div className="consent-button-container">
                            <button
                              type="button"
                              onClick={() => {
                                setConsentModalMode("new");
                                setConsentModalOpened(true);
                              }}
                              className="fill-consent-button"
                            >
                              <span>Fill the consent form</span>
                              <ArrowRight size={20} />
                            </button>
                            {hasAttemptedSubmit && !consentSubmitted && (
                              <span className="error">You must complete the consent form</span>
                            )}
                        </div>
                        ) : (
                          <div className="consent-acknowledgment">
                            <p>
                              By continuing, you agree and acknowledge to Syndicate Bio's{" "}
                              <span
                                onClick={handleEditConsent}
                                className="consent-link"
                              >
                                Consent Form.
                              </span>
                            </p>
                          </div>
                        )}
                      </motion.div>
                    </motion.form>
                  </motion.div>
                )}
                {apiError && (
                  <motion.p className="error-message" variants={itemVariants}>
                    {apiError}
                  </motion.p>
                )}
                {!showUploadForm && showGeneralError && (
                  <motion.div 
                    className="general-error-message"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    Fill required fields and fill in the correct manner
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>

          {!showUploadForm && (
            <motion.div variants={itemVariants} className="button-group">
              {submissionSuccess ? (
                <p className="submission-success-message">
                  Form Submitted Successfully.
                </p>
              ) : (
                <motion.button
                  type="submit"
                  onClick={handleSubmit}
                  className={`continue-button ${
                    isLoading || isFormInvalid() ? "disabled" : ""
                  }`}
                  disabled={isLoading || isFormInvalid()}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  {isLoading ? "Submitting..." : "Submit Form"}
                </motion.button>
              )}
            </motion.div>
          )}
        </motion.div>
      ) : userRole === "provider" ? (
        <ProviderRequisition />
      ) : (
        <Loading />
      )}

      {/* Consent Form Modal */}
      <Modal
        opened={consentModalOpened}
        onClose={() => {
          setConsentModalOpened(false);
          setConsentModalMode("new");
        }}
        withCloseButton={false}
        centered
        size="lg"
        className="consent-modal"
        overlayProps={{
          opacity: 0.55,
          blur: 3,
        }}
      >
        <PatientConsentForm
          onClose={() => {
            setConsentModalOpened(false);
            setConsentModalMode("new");
          }}
          onSubmit={handleConsentSubmit}
          isEditMode={consentModalMode === "edit"}
          existingData={consentModalMode === "edit" ? existingConsentData : null}
        />
      </Modal>
    </div>
  );
};

export default PatientRequisition;